import { Box, Chip, Typography } from '@mui/material';
import React from 'react';

import { canRenderFormNatively, InteractionForm } from './InteractionForm';
import { SectionGroup } from './SectionGroup';
import { FormObject, ServiceObject } from '../../../../@types';
import {
  InteractionFormDensity,
  InteractionFormShell,
  InteractionNotice,
} from '../../../../components/molecules/InteractionFormShell';
import { FormInterceptor } from '../../UpdateService/ServiceActionBarFragment/FormInterceptor';

const NOOP_DAMAGED = () => undefined;
const NOOP_ADD_FORM = () => undefined;

export interface InteractionsPanelProps {
  forms: FormObject[];
  mandatoryCount: number;
  unfilledMandatoryCount: number;
  serviceId?: number;
  serviceProxy: ServiceObject;
  onChangeForm: (idx: number, form: FormObject) => void;
  blockedMessage?: string;
}

export const InteractionsPanel: React.FC<InteractionsPanelProps> = ({
  forms,
  mandatoryCount,
  unfilledMandatoryCount,
  serviceId,
  serviceProxy,
  onChangeForm,
  blockedMessage,
}) => {
  // Legacy FormInterceptor's Photo/File fragments call uploadFiles(service.id),
  // so once the BFF has issued a real id we splice it onto the proxy.
  const effectiveService: ServiceObject = serviceId
    ? { ...serviceProxy, id: serviceId }
    : serviceProxy;
  if (forms.length === 0) {
    return (
      <SectionGroup title="Interaction Forms">
        <Box
          sx={{
            p: 2,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
            fontSize: '13px',
          }}
        >
          <Typography sx={{ fontSize: '13px' }}>
            No interaction forms required for the current selection.
          </Typography>
        </Box>
      </SectionGroup>
    );
  }

  return (
    <SectionGroup title="Interaction Forms">
      {mandatoryCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={
              unfilledMandatoryCount > 0
                ? `${unfilledMandatoryCount} of ${mandatoryCount} mandatory still required`
                : `${mandatoryCount} mandatory · ready`
            }
            size="small"
            sx={{
              height: 20,
              fontSize: '10px',
              bgcolor: unfilledMandatoryCount > 0 ? 'error.main' : 'success.main',
              color: 'common.white',
            }}
          />
        </Box>
      )}
      {blockedMessage && <InteractionNotice>{blockedMessage}</InteractionNotice>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {forms.map((form, idx) => (
          <InteractionFormShell
            key={form.id ?? form.ruleId ?? idx}
            title={form.title}
            isMandatory={form.isMandatory}
            density={InteractionFormDensity.Compact}
          >
            {canRenderFormNatively(form) ? (
              <InteractionForm
                form={form}
                serviceId={serviceId}
                service={effectiveService}
                onChange={(updated) => onChangeForm(idx, updated)}
              />
            ) : (
              <FormInterceptor
                service={effectiveService}
                value={form}
                onChange={(updated) => onChangeForm(idx, updated)}
                onDamagedPartSelect={NOOP_DAMAGED}
                onAddForm={NOOP_ADD_FORM}
                forms={forms}
              />
            )}
          </InteractionFormShell>
        ))}
      </Box>
    </SectionGroup>
  );
};
